// BackendPageForTerms.js — WorksForYou Terms of Service.
//
// Starter draft for an Italian sole-proprietor consultancy with a brochure
// website. Concrete client engagements are governed by separate written
// agreements, not by these terms. Have a lawyer review before publishing.

import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ConfigContext } from '../../App';
import { Navigation, Footer } from '../../myReactLib/MainBlocks';

const LAST_UPDATED = 'June 12, 2026';

function BackendPageForTerms() {
  const { config, homepageData, contactData } = useContext(ConfigContext);
  if (!homepageData) return null;

  return (
    <div className="w4u-page">
      <Navigation menuBar={homepageData.menu_bar} prefix={config.PREFIX} />
      <main className="w4u-main">
        <article className="w4u-legal">
          <header className="w4u-legal__header">
            <h1 className="w4u-legal__title">Terms of Service</h1>
            <p className="w4u-legal__date">Last updated: {LAST_UPDATED}</p>
          </header>

          <section className="w4u-legal__section">
            <h2>1. Acceptance</h2>
            <p>
              By accessing or using the WorksForYou website (the "Site"), you agree to these Terms of
              Service. If you do not agree, please do not use the Site.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>2. About this Site</h2>
            <p>
              WorksForYou is a sole proprietorship operated by Eugenio Ruggeri, based in Bologna, Italy.
              This Site describes our automation and software services. The Site itself does not
              constitute an offer or a contract — concrete client engagements are governed by separate
              written agreements between you and us.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>3. Tools connected to third-party platforms</h2>
            <p>
              Some services we build for clients connect to third-party platforms — for example
              Facebook and Instagram through Meta's official APIs. Those connections are authorized by
              the account owner through the platform's own login flow and remain governed by the
              platform's terms. Our handling of platform data is described in our{' '}
              <Link to="/privacy" className="w4u-legal__link">Privacy Policy</Link> and{' '}
              <Link to="/data-deletion" className="w4u-legal__link">Data Deletion Instructions</Link>.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>4. Intellectual property</h2>
            <p>
              All content on the Site — including text, layout, graphics, code, and the WorksForYou name
              and logo — is owned by us or our licensors. You may view and share links to the Site for
              personal or business research purposes, but you may not copy, reproduce, modify, or
              redistribute the content without our written permission.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>5. Acceptable use</h2>
            <p>
              You agree not to:
            </p>
            <ul>
              <li>use the Site for any unlawful purpose;</li>
              <li>attempt to disrupt or compromise the Site, its servers, or its security;</li>
              <li>scrape or systematically extract content without permission;</li>
              <li>impersonate any person or entity when communicating through the Site.</li>
            </ul>
          </section>

          <section className="w4u-legal__section">
            <h2>6. No warranty</h2>
            <p>
              The Site and its content are provided "as is" and "as available". We make reasonable efforts
              to keep the information accurate and up to date, but we do not warrant that it is complete,
              current, or error-free. The Site may be unavailable from time to time for maintenance or
              other reasons.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>7. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by Italian law, WorksForYou will not be liable for any
              indirect, incidental, or consequential loss arising from your use of the Site. Our total
              liability for any direct loss is limited to a reasonable, proportionate amount. Nothing in
              these terms limits liability for gross negligence, wilful misconduct, or anything else that
              cannot be limited by law.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>8. External links</h2>
            <p>
              The Site may contain links to third-party websites. We do not control those sites and are
              not responsible for their content, practices, or availability.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>9. Governing law and jurisdiction</h2>
            <p>
              These terms are governed by the laws of Italy. Any dispute arising in connection with the
              Site falls under the exclusive jurisdiction of the courts of Bologna, Italy, unless
              mandatory consumer-protection rules grant you the right to bring proceedings elsewhere.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>10. Changes</h2>
            <p>
              We may update these terms from time to time. The most recent revision date is shown at the
              top. Continued use of the Site after changes means you accept the updated terms.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>Contact</h2>
            <p>
              Questions about these terms? Email <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a>.
            </p>
          </section>
        </article>
      </main>
      <Footer menuBar={homepageData.menu_bar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

export default BackendPageForTerms;
